// ═══════════════════════════════════════════════════════════════════
//  modules/ah/ah.js
//  ACTIONS · ETATS · RAISONS_BOITE · helpers d'affichage
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
//  MODULE HÔTEL DES VENTES (intranet)
//  Lecture seule : tables oa_* du plugin OrialisAH.
//
//  ⚠️ Aucune action ici, et ce n'est pas un oubli. Annuler une annonce
//  depuis le site toucherait les mêmes lignes que le SELECT ... FOR UPDATE
//  de l'achat, qui tourne sur DEUX serveurs de jeu à la fois, sans en
//  avoir l'ordre de verrouillage. Les actions passent par /ah admin.
// ═══════════════════════════════════════════════════════════════════════

// Les quatre libellés relevés en base le 4 septembre 2026. La liste n'est
// PAS exhaustive par construction : le plugin peut en écrire d'autres, et
// `actionInfo` rend alors le jeton brut plutôt qu'une case vide.
export const ACTIONS = {
  DEPOT:      { label: 'Mise en vente', emoji: '📦', color: '#3DDC84' },
  ACHAT:      { label: 'Achat',         emoji: '🛒', color: '#4F8EF7' },
  EXPIRATION: { label: 'Expiration',    emoji: '⏳', color: '#E3B341' },
  RETRAIT:    { label: 'Retrait',       emoji: '📤', color: '#7D8590' },
};

export function actionInfo(a) {
  return ACTIONS[a] || { label: a, emoji: '❔', color: '#7D8590', inconnue: true };
}

export const ETATS = {
  EN_VENTE: { label: 'En vente', color: '#3DDC84', fond: '#0D3320' },
  EPUISEE:  { label: 'Épuisée',  color: '#7D8590', fond: '#161B22' },
  EXPIREE:  { label: 'Expirée',  color: '#E3B341', fond: '#3D2D00' },
  RETIREE:  { label: 'Retirée',  color: '#7D8590', fond: '#161B22' },
  ANNULEE:  { label: 'Annulée',  color: '#F85149', fond: '#3D1515' },
};

export function etatInfo(e) {
  return ETATS[e] || { label: e || '—', color: '#7D8590', fond: '#161B22' };
}

export const RAISONS_BOITE = {
  ACHAT:      'Acheté',
  EXPIRATION: 'Annonce expirée',
  RETRAIT:    'Annonce retirée',
  ADMIN:      'Geste du staff',
};

// ── Ce que renvoie une route qui a échoué ────────────────────────────
//  Toutes les routes du module répondent { error: "..." } quand elles
//  tombent. Sans ce garde-fou, le premier `data.somme.unites` sur une
//  réponse d'erreur lève une exception — et un plantage React ne casse
//  pas le composant fautif, il DÉMONTE TOUT L'ARBRE. L'utilisateur voit
//  une page blanche, sans message, sans rien à raconter au support.
//
//  Constaté le 4 septembre sur l'onglet des boîtes. La leçon est la même
//  que celle du carnet vide en août : une erreur qui ne se dit pas coûte
//  plus cher que l'erreur elle-même.
export function erreurDe(data) {
  if (!data) return 'Erreur de chargement — la route n\'a pas répondu.';
  if (data.error) return data.error;
  return null;
}

// ── Le nom des objets ────────────────────────────────────────────────
//  `oa_annonce.identifiant_lisible` porte l'identifiant Minecraft brut —
//  NETHER_WART, EMERALD — malgré son nom de colonne. C'est lisible pour
//  qui code, pas pour qui lit un journal.
//
//  ⚠ La vraie correction est côté plugin : écrire le nom d'affichage de
//  l'objet, celui que le joueur voit en jeu, qui gère aussi les objets
//  personnalisés et les objets renommés. En attendant, on traduit ici.
//
//  Deux étages, et le second ne peut pas se tromper :
//   1. le dictionnaire ci-dessous, pour ce qui se vend vraiment ;
//   2. sinon NETHER_WART -> « Nether Wart ». Jamais faux, juste anglais.
//
//  Compléter le dictionnaire au fil de ce qui apparaît dans le journal
//  est sans risque : une clé absente retombe simplement sur l'étage 2.
export const NOMS = {
  // Minerais et lingots
  COAL: 'Charbon', RAW_IRON: 'Fer brut', IRON_INGOT: 'Lingot de fer',
  RAW_GOLD: 'Or brut', GOLD_INGOT: "Lingot d'or", RAW_COPPER: 'Cuivre brut',
  COPPER_INGOT: 'Lingot de cuivre', DIAMOND: 'Diamant', EMERALD: 'Émeraude',
  LAPIS_LAZULI: 'Lapis-lazuli', REDSTONE: 'Redstone', QUARTZ: 'Quartz du Nether',
  NETHERITE_INGOT: 'Lingot de netherite', NETHERITE_SCRAP: 'Débris de netherite',
  ANCIENT_DEBRIS: 'Débris antique', AMETHYST_SHARD: "Éclat d'améthyste",
  // Blocs de rangement
  IRON_BLOCK: 'Bloc de fer', GOLD_BLOCK: "Bloc d'or", DIAMOND_BLOCK: 'Bloc de diamant',
  EMERALD_BLOCK: "Bloc d'émeraude", NETHERITE_BLOCK: 'Bloc de netherite',
  COPPER_BLOCK: 'Bloc de cuivre', REDSTONE_BLOCK: 'Bloc de redstone',
  LAPIS_BLOCK: 'Bloc de lapis-lazuli', COAL_BLOCK: 'Bloc de charbon',
  // Pierre et construction
  STONE: 'Pierre', COBBLESTONE: 'Roche', DEEPSLATE: 'Ardoise des abîmes',
  ANDESITE: 'Andésite', DIORITE: 'Diorite', GRANITE: 'Granite',
  SAND: 'Sable', GRAVEL: 'Gravier', CLAY_BALL: 'Argile', BRICK: 'Brique',
  GLASS: 'Verre', OBSIDIAN: 'Obsidienne', CRYING_OBSIDIAN: 'Obsidienne pleureuse',
  // Bois
  OAK_LOG: 'Bûche de chêne', SPRUCE_LOG: "Bûche de sapin", BIRCH_LOG: 'Bûche de bouleau',
  JUNGLE_LOG: 'Bûche de acajou', ACACIA_LOG: 'Bûche d\'acacia',
  DARK_OAK_LOG: 'Bûche de chêne noir', CHERRY_LOG: 'Bûche de cerisier',
  MANGROVE_LOG: 'Bûche de palétuvier', OAK_PLANKS: 'Planches de chêne',
  // Culture et nourriture
  WHEAT: 'Blé', WHEAT_SEEDS: 'Graines de blé', CARROT: 'Carotte',
  POTATO: 'Pomme de terre', BEETROOT: 'Betterave', SUGAR_CANE: 'Canne à sucre',
  MELON_SLICE: 'Tranche de pastèque', PUMPKIN: 'Citrouille', APPLE: 'Pomme',
  GOLDEN_APPLE: "Pomme dorée", ENCHANTED_GOLDEN_APPLE: 'Pomme dorée enchantée',
  BREAD: 'Pain', COCOA_BEANS: 'Fèves de cacao', BAMBOO: 'Bambou',
  KELP: 'Varech', HONEYCOMB: 'Rayon de miel',
  // Nether et End
  NETHER_WART: 'Verrue du Nether', BLAZE_ROD: 'Bâton de blaze',
  BLAZE_POWDER: 'Poudre de blaze', GHAST_TEAR: 'Larme de ghast',
  MAGMA_CREAM: 'Crème de magma', GLOWSTONE_DUST: 'Poudre de pierre lumineuse',
  SHULKER_SHELL: 'Carapace de shulker', ENDER_PEARL: 'Perle de l\'Ender',
  CHORUS_FRUIT: 'Fruit de chorus', NETHER_STAR: 'Étoile du Nether',
  // Butin de créatures
  BONE: 'Os', STRING: 'Ficelle', SPIDER_EYE: "Œil d'araignée",
  GUNPOWDER: 'Poudre à canon', ROTTEN_FLESH: 'Chair putréfiée',
  LEATHER: 'Cuir', FEATHER: 'Plume', SLIME_BALL: 'Boule de slime',
  PHANTOM_MEMBRANE: 'Membrane de phantom', PRISMARINE_SHARD: 'Éclat de prismarine',
  // Divers
  BOOK: 'Livre', ENCHANTED_BOOK: 'Livre enchanté', PAPER: 'Papier',
  EXPERIENCE_BOTTLE: "Fiole d'expérience", TOTEM_OF_UNDYING: "Totem d'immortalité",
  ELYTRA: 'Élytres', TRIDENT: 'Trident', HEART_OF_THE_SEA: 'Cœur de la mer',
  NAUTILUS_SHELL: 'Coquille de nautile', SADDLE: 'Selle',
};

export function nomObjet(identifiant) {
  if (!identifiant) return '—';
  const cle = String(identifiant).trim();
  if (NOMS[cle]) return NOMS[cle];
  // L'étage 2 : on enlève l'éventuel espace de nom (orialis:golem_gardien),
  // on remplace les tirets bas, et on met une majuscule par mot.
  const sansPrefixe = cle.includes(':') ? cle.split(':').pop() : cle;
  if (/[a-z]/.test(sansPrefixe) && !/^[A-Z_]+$/.test(sansPrefixe) && sansPrefixe !== sansPrefixe.toLowerCase()) {
    return sansPrefixe;   // deja un nom lisible, on n'y touche pas
  }
  return sansPrefixe.toLowerCase().split('_').filter(Boolean)
    .map((m) => m.charAt(0).toUpperCase() + m.slice(1)).join(' ');
}

// ── Mise en forme ────────────────────────────────────────────────────
export function nombre(v, decimales = 0) {
  const n = Number(v || 0);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}

export function argent(v) {
  if (v === null || v === undefined) return '—';
  return nombre(v, 2) + ' $';
}

// Le journal se lit à la seconde près : c'est ce qui permet de dire que
// trois lignes appartiennent au même achat.
export function horodate(d) {
  if (!d) return '—';
  const j = new Date(d);
  if (Number.isNaN(j.getTime())) return '—';
  return j.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export function dateCourte(d) {
  if (!d) return '—';
  const j = new Date(d);
  if (Number.isNaN(j.getTime())) return '—';
  return j.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Aujourd'hui et hier au format que les champs <input type="date"> attendent.
export function jourISO(decalage = 0) {
  const d = new Date();
  d.setDate(d.getDate() + decalage);
  return d.toISOString().slice(0, 10);
}
